
import React from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useInternalAuth } from '@/hooks/useInternalAuth';
import { Navigate } from 'react-router-dom';
import { isAccountPaid } from '@/utils/rbac';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { AlertTriangle, Shield } from 'lucide-react';

interface AuthGuardProps {
  children: React.ReactNode;
  requireAuth?: boolean;
  allowedRoles?: string[];
  requiresPaidAccount?: boolean;
  requiresAdminAccess?: boolean;
}

const AuthGuard: React.FC<AuthGuardProps> = ({ 
  children, 
  requireAuth = true,
  allowedRoles = [],
  requiresPaidAccount = false,
  requiresAdminAccess = false
}) => {
  const { user: supabaseUser, profile, loading: supabaseLoading, initialCheckComplete: supabaseInitialCheck } = useAuth();
  const { user: internalUser, loading: internalLoading, initialCheckComplete: internalInitialCheck } = useInternalAuth();

  // Wait for both auth systems to complete their initial checks
  const bothSystemsReady = supabaseInitialCheck && internalInitialCheck;
  const anySystemLoading = supabaseLoading || internalLoading;

  // Show loading screen with better UX
  if (!bothSystemsReady || anySystemLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-black">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white mx-auto mb-4"></div>
          <div className="text-white">Authenticating...</div>
        </div>
      </div>
    );
  }

  const isAuthenticated = !!(supabaseUser || internalUser);
  const userRole = profile?.role || internalUser?.role;

  if (requireAuth && !isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Check for admin access requirement
  if (requiresAdminAccess && userRole !== 'admin') {
    return (
      <div className="flex items-center justify-center min-h-screen bg-black">
        <Card className="bg-red-900/20 border-red-500 max-w-md">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-red-400">
              <Shield className="h-5 w-5" />
              Administrator Access Required
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-red-300 mb-4">
              This area is restricted to administrator accounts only.
            </p>
            <p className="text-gray-400 text-sm">
              Your current role: {userRole || 'unknown'}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (allowedRoles.length > 0 && userRole && !allowedRoles.includes(userRole)) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-black">
        <Card className="bg-red-900/20 border-red-500 max-w-md">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-red-400">
              <AlertTriangle className="h-5 w-5" />
              Access Restricted
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-red-300 mb-4">
              Your account type ({userRole}) doesn't have access to this content.
            </p>
            <p className="text-gray-400 text-sm">
              Allowed roles: {allowedRoles.join(', ')}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (requiresPaidAccount && userRole) {
    const isPaidAccount = isAccountPaid(userRole);
    const hasPaidFeatures = isPaidAccount && profile?.fee_paid;
    
    if (!hasPaidFeatures) {
      return (
        <div className="flex items-center justify-center min-h-screen bg-black">
          <div className="text-center">
            <h2 className="text-xl font-bold text-white mb-2">
              {isPaidAccount ? 'Payment Required' : 'Upgrade Required'}
            </h2>
            <p className="text-gray-400 mb-4">
              {isPaidAccount 
                ? 'This feature requires payment completion for your paid account.'
                : 'This feature requires a paid account (Coach, Team Owner, or Venue).'
              }
            </p>
            <button className="bg-sports-green text-black px-6 py-2 rounded-lg font-medium">
              {isPaidAccount ? 'Complete Payment' : 'Upgrade Account'}
            </button>
          </div>
        </div>
      );
    }
  }

  return <>{children}</>;
};

export default AuthGuard;
